<script setup lang="ts">
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useDescriptor from '~/composables/useDescriptor'
import { useContentHelper } from '~/composables/useContentHelper'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'

const props = withDefaults(defineProps<{
  resource: BaseItem
  loading?: 'eager' | 'lazy'
  showLabel?: boolean
}>(), {
  loading: 'lazy',
  showLabel: true,
})

const { getTitle } = useContentHelper()
const { buildResourceUrl } = useUrlBuilder()
const { getDescriptor } = useDescriptor()
const { t } = useI18n()

const title = computed(() => getTitle(props.resource))
const link = computed(() => buildResourceUrl(props.resource))
const label = getDescriptor(props.resource)

const shouldShowNew = computed(() => props.resource.new && !props.resource.coming)
const shouldShowComing = computed(() => props.resource.coming && !props.resource.new)
</script>

<template>
  <article
    class="group relative flex h-full flex-col rounded-lg bg-(--theme-5) p-4 text-(--theme-60) has-focus-visible:ring"
    :class="{
      'animate-fade transition hover:bg-(--theme-10) hover:shadow-2xl': !shouldShowComing,
      'before:pointer-events-none before:absolute before:inset-0 before:z-10 before:rounded-lg before:bg-black/50': shouldShowComing,
    }"
  >
    <aside
      v-if="shouldShowNew"
      class="bg-green-40 pointer-events-none absolute top-0 right-0 z-20 rounded-bl-2xl p-1.5 pl-2 text-sm font-medium tracking-wider text-white uppercase"
      data-testid="new-badge"
      v-text="t('newShort')"
    />
    <aside
      v-if="shouldShowComing"
      class="pointer-events-none absolute top-0 right-0 z-30 rounded-bl-2xl bg-(--theme-pop) p-1.5 pl-2 text-sm font-medium tracking-wider text-(--theme-60) uppercase"
      v-text="t('coming')"
    />
    <div
      v-if="showLabel || resource.tiered"
      class="mb-1 flex gap-1 self-start text-xs uppercase"
    >
      <span
        v-if="showLabel"
        v-text="label"
      />
      <span
        v-if="resource.tiered"
        v-text="t('search.checkboxes.tiered')"
      />
    </div>
    <div :class="{ 'my-auto': resource.subtitle }">
      <h3 class="mb-3 self-start text-lg leading-tight font-semibold">
        <LinkOrAnchor
          v-if="!shouldShowComing"
          class="line-clamp-3 before:absolute before:inset-0 before:rounded-2xl before:transition before:hover:shadow-lg"
          :link="link"
        >
          {{ title }}
        </LinkOrAnchor>
        <span
          v-else
          class="line-clamp-3"
        >
          {{ title }}
        </span>
      </h3>
      <div v-if="!resource.subtitle" class="pointer-events-none relative aspect-[6/3] overflow-hidden rounded bg-(--theme-20)">
        <img
          v-if="resource.thumbnail"
          class="size-full rounded object-cover"
          :class="{
            'transition duration-200 group-hover:scale-110': !shouldShowComing,
          }"
          data-testid="thumbnail-img"
          :src="resource.thumbnail"
          :alt="resource.thumbnailAlt || ''"
          :loading
        >
        <div
          v-if="!resource.thumbnail"
          class="absolute top-0 right-0"
          data-testid="thumbnail-decoration"
        >
          <!-- Outer triangle -->
          <div class="absolute top-0 right-0 size-12 bg-(--theme-pop)" style="clip-path: polygon(0% 100%, 0% 0%, 100% 100%)" />
          <div class="absolute top-0 right-0 size-0 border-t-[48px] border-l-[48px] border-t-(--theme-5) border-l-transparent shadow-lg" />
        </div>
      </div>
      <p
        v-else
        class="line-clamp-3"
        data-testid="subtitle"
        v-text="resource.subtitle"
      />
    </div>
    <CardMenu
      :resource="resource"
      toggle-shade="dark"
      button-classes="z-10 mt-1"
    />
  </article>
</template>
